import { byCssClass } from "../common/dom-util.ts";

const SearchBar = {
  input: "portal-searchbar__input",
  icon: "portal-searchbar__icon",
  wrapper: "portal-searchbar",
  extended: "portal-searchbar--expanded",
  manualClosed: "portal-searchbar--manual-closed",
  formRecordColumn: "form-record__column",
};

const PageContent = {
  main: "base-wrapper",
};

const Toolbar = {
  main: "toolbar",
  searching: "toolbar--searching",
};

export function initSearchbar(): void {
  addEventListenerForSearchBar();
  observeSearchBar();
}

function addEventListenerForSearchBar(): void {
  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("keydown", handleDocumentClick);
}

function checkAndSetSearchBarClass(): void {
  const searchBar = byCssClass(SearchBar.input);
  const searchBarWrapper = byCssClass(SearchBar.wrapper);
  const toolbar = byCssClass(Toolbar.main);
  if (
    !(searchBar instanceof HTMLInputElement) ||
    !(searchBarWrapper instanceof HTMLElement) ||
    !(toolbar instanceof HTMLElement)
  ) {
    return;
  }

  if (searchBar.value.trim() !== "" && !searchBarWrapper.classList.contains(SearchBar.manualClosed)) {
    searchBarWrapper.classList.add(SearchBar.extended);
    toolbar.classList.add(Toolbar.searching);
  }
}

function observeSearchBar(): void {
  const parentElement = byCssClass(PageContent.main);

  if (!(parentElement instanceof HTMLElement)) {
    return;
  }

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        const searchBarWrapper = byCssClass(SearchBar.wrapper);
        if (searchBarWrapper) {
          checkAndSetSearchBarClass();
          break;
        }
      }
    }
  });

  observer.observe(parentElement, { childList: true, subtree: true });
}

function handleDocumentClick(event: Event): void {
  const searchBar = byCssClass(SearchBar.input);
  const searchBarIcon = byCssClass(SearchBar.icon);
  const searchBarWrapper = byCssClass(SearchBar.wrapper);
  const toolbar = byCssClass(Toolbar.main);

  if (
    !(searchBar instanceof HTMLInputElement) ||
    !(searchBarIcon instanceof HTMLElement) ||
    !(searchBarWrapper instanceof HTMLElement) ||
    !(toolbar instanceof HTMLElement)
  ) {
    return;
  }

  const isPointer = event instanceof PointerEvent && event.target instanceof HTMLElement;
  const isKeyboard =
    event instanceof KeyboardEvent &&
    event.target instanceof HTMLElement &&
    (event.key === "Enter" || event.key === "Escape" || event.key === " " || event.key === "Spacebar");

  if (!isPointer && !isKeyboard) {
    return;
  }

  const isOpen = searchBarWrapper.classList.contains(SearchBar.extended);
  const isManualClose = isManualClosed(event, searchBarIcon);
  const isOutside = isClickOrKeydownOutsideSearchBar(event, searchBar, searchBarIcon, searchBarWrapper);

  if (isKeyboard && isOutside) {
    return;
  }

  if (isOutside && searchBar.value.trim() === "") {
    closeSearchbar(searchBarWrapper, toolbar);
    return;
  }

  if (isOpen && isManualClose) {
    closeSearchbar(searchBarWrapper, toolbar);
    searchBarWrapper.classList.add(SearchBar.manualClosed);
  } else {
    openSearchbar(searchBarWrapper, toolbar);
  }

  if (searchBarWrapper.classList.contains(SearchBar.extended)) {
    searchBar.focus();
    searchBarWrapper.classList.remove(SearchBar.manualClosed);
  }
}

function isManualClosed(event: PointerEvent | KeyboardEvent, searchBarIcon: HTMLElement): boolean {
  return (
    (event instanceof KeyboardEvent && event.key === "Escape") ||
    (event instanceof PointerEvent && event.target === searchBarIcon)
  );
}

function isClickOrKeydownOutsideSearchBar(
  event: PointerEvent | KeyboardEvent,
  searchBar: HTMLInputElement,
  searchBarIcon: HTMLElement,
  searchBarWrapper: HTMLElement,
): boolean {
  return !(event.target === searchBar || event.target === searchBarIcon || event.target === searchBarWrapper);
}

/**
 * Closes the search bar programmatically.
 * @param searchBarWrapper The wrapper element of the search bar.
 * @param toolbar The toolbar with the search bar.
 */
function closeSearchbar(searchBarWrapper: HTMLElement, toolbar: HTMLElement): void {
  searchBarWrapper?.classList.remove(SearchBar.extended);
  toolbar?.classList.remove(Toolbar.searching);
  searchBarWrapper?.setAttribute("aria-expanded", "false");
}

/**
 * Opens the search bar programmatically.
 * @param searchBarWrapper The wrapper element of the search bar.
 * @param toolbar The toolbar with the search bar.
 */
function openSearchbar(searchBarWrapper: HTMLElement, toolbar: HTMLElement): void {
  searchBarWrapper?.classList.add(SearchBar.extended);
  toolbar?.classList.add(Toolbar.searching);
  searchBarWrapper?.setAttribute("aria-expanded", "true");
}
